import { useEffect, useState } from 'react';
import type { CallResponse, LogActivityInput } from '@orion/contracts';
import { ApiError } from '../../lib/api-client.js';
import { useToast } from '../../lib/toast.js';
import { useCallQuery, useCallSettingsQuery, usePlaceCall } from '../admin/integrations/calling-api.js';
import { useLogActivity, usePulseReplyContextQuery, useSendPulseCompose, useSendPulseReply } from './api.js';

type Kind = LogActivityInput['kind'];

const CALL_STATUS_LABEL: Record<CallResponse['status'], string> = {
  initiated: 'Calling your phone…',
  ringing: 'Ringing your phone…',
  'in-progress': 'Connected — call in progress…',
  completed: 'Call ended',
  'no-answer': 'No answer',
  busy: 'Line busy',
  failed: 'Call failed',
  canceled: 'Call canceled',
};
const TERMINAL_CALL_STATUSES: CallResponse['status'][] = ['completed', 'failed', 'no-answer', 'busy', 'canceled'];

function formatDuration(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** The real dial-out — rings the rep's own saved bridge number first, then
 * connects them to the lead (docs "Calling & meetings"). Self-contained:
 * it never touches the composer's `body`/`submit` state, so the manual
 * Outcome/Duration/notes form below still works independently (e.g. to log
 * a call placed from an actual phone, outside the app). */
interface CallTarget {
  leadId?: string;
  contactId?: string;
  companyId?: string;
  /** "lead" | "contact" | "company" — just for the button's copy. */
  label: string;
}

function CallNowControl({ target, bridgePhoneNumber }: { target: CallTarget; bridgePhoneNumber: string }) {
  const placeCall = usePlaceCall();
  const [callId, setCallId] = useState<string | null>(null);
  const { data: call } = useCallQuery(callId);
  const toast = useToast();

  const start = async () => {
    try {
      const result = await placeCall.mutateAsync({ leadId: target.leadId, contactId: target.contactId, companyId: target.companyId });
      setCallId(result.id);
    } catch (err) {
      toast.error(err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Failed to start the call.');
    }
  };

  if (!call) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-control border border-border-strong bg-surface-subtle px-2.5 py-2">
        <span className="text-xs text-text-muted">Rings {bridgePhoneNumber} first, then connects you to the {target.label}.</span>
        <button
          type="button"
          onClick={start}
          disabled={placeCall.isPending}
          className="flex h-7 shrink-0 items-center rounded-control bg-primary px-3 text-xs font-semibold text-white hover:bg-primary-hover disabled:opacity-50"
        >
          {placeCall.isPending ? 'Calling…' : 'Call'}
        </button>
      </div>
    );
  }

  const terminal = TERMINAL_CALL_STATUSES.includes(call.status);
  return (
    <div className="flex items-center justify-between gap-2 rounded-control border border-primary-border bg-primary-tint px-2.5 py-2">
      <span className="text-xs font-medium text-ink">{CALL_STATUS_LABEL[call.status]}</span>
      {terminal && call.durationSeconds != null && <span className="text-xs text-text-muted">{formatDuration(call.durationSeconds)}</span>}
    </div>
  );
}

const KIND_TABS: Array<{ key: Kind; label: string; path: string; placeholder: string }> = [
  { key: 'note', label: 'Note', path: 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9zM14 3v6h6', placeholder: 'Add a note, @mention a teammate…' },
  { key: 'call', label: 'Call', path: 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2', placeholder: 'Log a call, @mention a teammate…' },
  { key: 'email', label: 'Email', path: 'M3 5h18v14H3z M3 7l9 6 9-6', placeholder: 'Log an email…' },
  { key: 'meeting', label: 'Meeting', path: 'M3 10h18M16 3v4M8 3v4 M3 5h18v16H3z', placeholder: 'Log a meeting…' },
];
const OUTCOMES = ['Connected', 'No answer', 'Left voicemail', 'Bad number'];
const DURATIONS = [5, 15, 30, 60];

interface ComposerProps {
  leadId?: string;
  contactId?: string;
  companyId?: string;
  opportunityId?: string;
  /** The record's phone number — only used to offer the real "Call"
   * button (lead, contact or company; `Call`'s own schema has no
   * opportunityId). Never sent to the API; the backend resolves the
   * number itself server-side from whichever id was given. */
  phone?: string | null;
  /** Set by Timeline's "Resend" button — a new object each time (even with
   * identical text) so the effect below re-applies it. */
  prefill?: { kind: Kind; body: string } | null;
  /** Called right after `prefill` is applied, so the parent can clear it —
   * otherwise a tab switch that unmounts/remounts this composer (Overview's
   * conditional render) would resurrect stale resend text on top of an
   * unrelated later send. */
  onPrefillConsumed?: () => void;
}

/** design/orion-crm-design/rendered-html/LeadDetail.html "Log activity"
 * card — real, not a UI-only mock: posts to POST /activities/log, which
 * writes a genuine Activity row with the record link, actor, and (for
 * calls) outcome/duration. */
export function LogActivityComposer({ prefill, onPrefillConsumed, phone, ...props }: ComposerProps) {
  const [kind, setKind] = useState<Kind>('call');
  const [body, setBody] = useState('');
  const [outcome, setOutcome] = useState(OUTCOMES[0]!);
  const [duration, setDuration] = useState(DURATIONS[1]!);
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  const [durationOpen, setDurationOpen] = useState(false);
  const logActivity = useLogActivity();
  const toast = useToast();
  const { data: replyContext } = usePulseReplyContextQuery(props.leadId);
  const sendPulseReply = useSendPulseReply(props.leadId);
  const sendPulseCompose = useSendPulseCompose(props.leadId);
  const canSendReply = kind === 'email' && Boolean(props.leadId) && replyContext?.canReply === true;
  // A lead with no Inbox Pulse thread yet (created via the MCP tools, a web
  // form, or anything else that isn't an inbound email) can still have a
  // first email sent to it — `canCompose` is the signed-in member having at
  // least one of their own active mailboxes to send it from.
  const canCompose = kind === 'email' && Boolean(props.leadId) && !canSendReply && replyContext?.canCompose === true;
  const tab = KIND_TABS.find((t) => t.key === kind)!;
  const mailboxOptions = replyContext?.options;
  const composeMailboxOptions = replyContext?.composeOptions;
  const [fromConnectionId, setFromConnectionId] = useState<string | undefined>(undefined);
  const [subject, setSubject] = useState('');
  const { data: callSettings } = useCallSettingsQuery();
  // Calling is lead/contact/company only — `Call`'s own schema has no
  // opportunityId (matches the exclusive-arc shape the DB enforces).
  const callTarget: CallTarget | null = props.leadId
    ? { leadId: props.leadId, label: 'lead' }
    : props.contactId
      ? { contactId: props.contactId, label: 'contact' }
      : props.companyId
        ? { companyId: props.companyId, label: 'company' }
        : null;
  const canCallNow = kind === 'call' && callTarget !== null && Boolean(phone) && callSettings?.callingEnabled === true && Boolean(callSettings?.bridgePhoneNumber);

  // Defaults to the most recent mailbox (`options[0]`, same one
  // `mailboxEmail` already names) every time the lead's set of eligible
  // mailboxes changes — re-picks if the previously-selected one drops out
  // (e.g. it got disconnected) rather than sending through a stale id.
  // `composeMailboxOptions` drives the same state for a lead with no thread
  // yet; the two are mutually exclusive (`canSendReply`/`canCompose` never
  // both true), so one effect covers both.
  useEffect(() => {
    const options = mailboxOptions ?? composeMailboxOptions;
    if (!options) {
      setFromConnectionId(undefined);
      return;
    }
    setFromConnectionId((current) => (current && options.some((o) => o.connectionId === current) ? current : options[0]?.connectionId));
  }, [mailboxOptions, composeMailboxOptions]);

  // Only re-runs when `prefill` itself changes (a fresh object per
  // "Resend" click) — `onPrefillConsumed` deliberately isn't a dependency,
  // since including it would require the parent to memoize it for no
  // benefit here.
  useEffect(() => {
    if (!prefill) return;
    setKind(prefill.kind);
    setBody(prefill.body);
    onPrefillConsumed?.();
  }, [prefill]);

  const submit = async () => {
    if (!body.trim()) return;
    if (canSendReply) {
      try {
        await sendPulseReply.mutateAsync({ bodyText: body.trim(), connectionId: mailboxOptions ? fromConnectionId : undefined });
        toast.success('Reply sent.');
        setBody('');
      } catch (err) {
        const detail = err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Failed to send reply.';
        toast.error(detail);
      }
      return;
    }
    if (canCompose) {
      if (!fromConnectionId || !subject.trim()) return;
      try {
        await sendPulseCompose.mutateAsync({ connectionId: fromConnectionId, subject: subject.trim(), bodyText: body.trim() });
        toast.success('Email sent.');
        setBody('');
        setSubject('');
      } catch (err) {
        const detail = err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Failed to send email.';
        toast.error(detail);
      }
      return;
    }
    await logActivity.mutateAsync({
      kind,
      bodyText: body.trim(),
      outcome: kind === 'call' ? outcome : undefined,
      durationSeconds: kind === 'call' ? duration * 60 : undefined,
      ...props,
    });
    setBody('');
  };

  return (
    <section aria-label="Log activity" className="flex shrink-0 flex-col rounded-card border border-border bg-surface shadow-card">
      <div role="tablist" aria-label="Activity type" className="flex h-9.5 items-end gap-4.5 border-b border-divider px-3">
        {KIND_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={kind === t.key}
            onClick={() => setKind(t.key)}
            className={`flex h-9.5 items-center gap-1.5 border-b-2 text-[13px] font-medium ${kind === t.key ? 'border-primary text-primary-text' : 'border-transparent text-text-muted hover:text-ink'}`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d={t.path} />
            </svg>
            {t.label}
          </button>
        ))}
      </div>
      <div className="flex flex-col gap-2.5 px-3 pb-3 pt-2.5">
        {canCallNow && <CallNowControl target={callTarget!} bridgePhoneNumber={callSettings!.bridgePhoneNumber!} />}
        {kind === 'email' && canCompose && (
          <label className="flex">
            <span className="sr-only">Subject</span>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              className="h-8 flex-1 rounded-control border border-border-strong bg-surface px-2.5 text-sm text-ink placeholder:text-text-muted focus-visible:outline-none focus-visible:shadow-focus-ring focus-visible:border-primary"
            />
          </label>
        )}
        <label className="flex">
          <span className="sr-only">{tab.label} notes</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={2}
            placeholder={canSendReply ? 'Write a reply…' : canCompose ? 'Write your email…' : tab.placeholder}
            className="h-13 flex-1 resize-none rounded-control border border-border-strong bg-surface px-2.5 py-2 text-sm text-ink placeholder:text-text-muted focus-visible:outline-none focus-visible:shadow-focus-ring focus-visible:border-primary"
          />
        </label>
        {kind === 'email' && canCompose && composeMailboxOptions && (
          <div className="flex flex-col gap-1">
            <label className="flex items-center gap-1.5 text-xs text-text-muted">
              Send as
              <select
                value={fromConnectionId ?? ''}
                onChange={(e) => setFromConnectionId(e.target.value)}
                className="h-6.5 min-w-0 flex-1 rounded-sm border border-border-strong bg-surface px-1.5 text-xs font-medium text-ink"
              >
                {composeMailboxOptions.map((o) => (
                  <option key={o.connectionId} value={o.connectionId}>
                    {o.emailAddress}
                  </option>
                ))}
              </select>
            </label>
            <p className="text-xs text-text-muted">Starts a new email thread with this lead.</p>
          </div>
        )}
        {kind === 'email' && canSendReply && mailboxOptions && (
          <div className="flex flex-col gap-1">
            <label className="flex items-center gap-1.5 text-xs text-text-muted">
              Sends as
              <select
                value={fromConnectionId ?? ''}
                onChange={(e) => setFromConnectionId(e.target.value)}
                className="h-6.5 min-w-0 flex-1 rounded-sm border border-border-strong bg-surface px-1.5 text-xs font-medium text-ink"
              >
                {mailboxOptions.map((o) => (
                  <option key={o.connectionId} value={o.connectionId}>
                    {o.emailAddress}
                  </option>
                ))}
              </select>
            </label>
            <p className="text-xs text-text-muted">Lands in this lead&apos;s inbox as a reply.</p>
          </div>
        )}
        {kind === 'email' && canSendReply && !mailboxOptions && (
          <p className="text-xs text-text-muted">Sends as {replyContext?.mailboxEmail} — lands in this lead&apos;s inbox as a reply.</p>
        )}
        {kind === 'email' && !canSendReply && !canCompose && (
          <p className="text-xs text-text-muted">
            {replyContext?.reason ? `Not connected to send: ${replyContext.reason} This only logs a record.` : 'This only logs a record — it does not send anything.'}
          </p>
        )}
        <div className="flex items-center gap-2">
          {kind === 'call' && (
            <>
              <div className="relative">
                <button type="button" onClick={() => setOutcomeOpen((o) => !o)} className="flex h-8 items-center gap-1 rounded-control border border-border-strong bg-surface px-2.5 text-[13px] text-text-secondary">
                  Outcome: <span className="font-semibold text-ink">{outcome}</span>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </button>
                {outcomeOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setOutcomeOpen(false)} />
                    <div className="absolute bottom-full left-0 z-50 mb-1 w-40 overflow-hidden rounded-dialog border border-border bg-surface py-1 shadow-dialog">
                      {OUTCOMES.map((o) => (
                        <button key={o} type="button" onClick={() => { setOutcome(o); setOutcomeOpen(false); }} className="block w-full px-3 py-1.5 text-left text-sm text-ink hover:bg-surface-subtle">
                          {o}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <div className="relative">
                <button type="button" onClick={() => setDurationOpen((o) => !o)} className="flex h-8 items-center gap-1 rounded-control border border-border-strong bg-surface px-2.5 text-[13px] text-text-secondary">
                  Duration: <span className="font-semibold text-ink">{duration} min</span>
                </button>
                {durationOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setDurationOpen(false)} />
                    <div className="absolute bottom-full left-0 z-50 mb-1 w-28 overflow-hidden rounded-dialog border border-border bg-surface py-1 shadow-dialog">
                      {DURATIONS.map((d) => (
                        <button key={d} type="button" onClick={() => { setDuration(d); setDurationOpen(false); }} className="block w-full px-3 py-1.5 text-left text-sm text-ink hover:bg-surface-subtle">
                          {d} min
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
          <div className="flex-1" />
          <button
            type="button"
            onClick={submit}
            disabled={
              !body.trim() ||
              logActivity.isPending ||
              sendPulseReply.isPending ||
              sendPulseCompose.isPending ||
              (canCompose && (!fromConnectionId || !subject.trim()))
            }
            className="flex h-8 items-center rounded-control bg-primary px-3.5 text-[13px] font-semibold text-white hover:bg-primary-hover disabled:opacity-50"
          >
            {canSendReply ? 'Send reply' : canCompose ? 'Send email' : `Log ${tab.label.toLowerCase()}`}
          </button>
        </div>
      </div>
    </section>
  );
}
